import { mapGetters, mapActions } from 'vuex';
import { ROLE_ADMIN_MITRA, ROLE_ADMIN_PJO, ROLE_HSE } from '@/constants/mitraRole';
import api from '@/api/mitra-tna-report/mitra-tna-report';
import mitraCourseListFilter from '@/mixins/mitraCourseListFilter';
import {
  APPROVAL_STEP_STATUS_OPTIONS,
  COURSE_TYPE_OPTIONS,
  TNA_COURSE_STATUS_OPTIONS
} from '@/constants/mitraFilters';

export default {
  name: 'MitraTNAReport',
  mixins: [mitraCourseListFilter],
  data() {
    return {
      selectedCourse: null,
      currentPage: 1,
      perPage: 10,
      perPageOptions: [10, 50, 100],
      filterModalId: 'modal-filter-mitra-tna-report',
      statusQueryKey: 'tna_status',
      filters: {
        search: '',
        company: null,
        status: null,
        approval_step: null,
        course_type: null
      },
      statusOptions: TNA_COURSE_STATUS_OPTIONS,
      approvalStepOptions: APPROVAL_STEP_STATUS_OPTIONS,
      courseTypeOptions: COURSE_TYPE_OPTIONS,
      imageBaseUrl: process.env.VUE_APP_API_URL_UGEMS_UNIV,
      companyOptions: [],
      companiesLoading: false,
      selectedPeriod: null,
      searchDebounceTimer: null,
      isInitializing: false
    };
  },
  computed: {
    ...mapGetters('mitraTnaReport', ['courses', 'pagination', 'loading', 'error', 'summary', 'summaryLoading']),
    ...mapGetters('tnaPeriod', { periods: 'periods', periodsLoading: 'loading' }),
    ...mapGetters('users', ['profileData']),
    
    // Period dropdown options from store
    periodOptions() {
      if (!this.periods || this.periods.length === 0) return [];
      
      return this.periods.map(p => ({
        label: this.formatPeriodLabel(p.start_date, p.end_date),
        value: p.id
      }));
    },
    
    isLoading() {
      return this.loading;
    },
    
    isHSERole() {
      const userRoles = [
        this.profileData?.role_id,
        ...(this.profileData?.roles_uenroll || []),
        ...(this.profileData?.roles_uenrol || []),
      ].filter(Boolean);
      return userRoles.includes(ROLE_HSE) && !userRoles.includes(ROLE_ADMIN_PJO) && !userRoles.includes(ROLE_ADMIN_MITRA);
    },
    
    isPJORole() {
      const userRoles = [
        this.profileData?.role_id,
        ...(this.profileData?.roles_uenroll || []),
        ...(this.profileData?.roles_uenrol || []),
      ].filter(Boolean);
      return userRoles.includes(ROLE_ADMIN_PJO);
    },
    
    filteredCourses() {
      let filtered = this.courses || [];
      
      if (this.filters.search) {
        const searchLower = this.filters.search.toLowerCase();
        filtered = filtered.filter(course => 
          course.title.toLowerCase().includes(searchLower) ||
          course.description?.toLowerCase().includes(searchLower) ||
          course.provider_name?.toLowerCase().includes(searchLower)
        );
      }
      
      return filtered;
    },
    
    paginatedCourses() {
      return this.courses || [];
    },
    
    totalItems() {
      return this.pagination?.total || 0;
    },
    
    totalPages() {
      return this.pagination?.totalPage || 0;
    },
    
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;
      
      // Always show at least page 1
      if (total <= 1) {
        pages.push(1);
      } else if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        
        if (current > 3) {
          pages.push('...');
        }
        
        const start = Math.max(2, current - 1);
        const end = Math.min(total - 1, current + 1);
        
        for (let i = start; i <= end; i++) {
          pages.push(i);
        }
        
        if (current < total - 2) {
          pages.push('...');
        }
        
        pages.push(total);
      }
      
      return pages;
    }
  },
  
  watch: {
    'filters.search'() {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.resetPageAndFetchCourses();
      }, 400);
    },
    'filters.company'() {
      if (this.isInitializing) return;
      this.currentPage = 1;
      this.fetchSummary();
      this.fetchCourses();
    },
    selectedPeriod() {
      if (this.isInitializing) return;
      this.currentPage = 1;
      this.fetchSummary();
      this.fetchCourses();
    },
    currentPage() {
      this.fetchCourses();
    },
    // Watch profileData to fetch companies when user data is loaded
    profileData: {
      handler(newVal) {
        if (newVal && this.isHSERole && this.companyOptions.length === 0) {
          this.fetchCompanies();
        }
      },
      immediate: true
    }
  },
  
  methods: {
    ...mapActions('mitraTnaReport', ['getCourses', 'getSummary']),

    resetPageAndFetchCourses() {
      if (this.currentPage === 1) {
        this.fetchCourses();
        return;
      }

      this.currentPage = 1;
    },

    goToDashboardReportTab() {
      this.$router.push({
        name: 'dashboard',
        query: { tab: 'report', reportType: 'tna' }
      });
    },
    
    async fetchCompanies() {
      try {
        this.companiesLoading = true;
        const response = await this.$store.dispatch('mitraTnaReport/getCompanies', {
          limit: 1000,
          is_external: true
        });
        
        if (response && response.data && Array.isArray(response.data)) {
          this.companyOptions = response.data;
        }
      } catch (error) {
        console.error('Error fetching companies:', error);
        this.$swal({
          title: 'Error',
          text: error.message || 'Daftar perusahaan belum berhasil dimuat. Silakan coba lagi.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
      } finally {
        this.companiesLoading = false;
      }
    },
    
    async fetchCourses() {
      try {
        const params = {
          page: this.currentPage,
          limit: this.perPage,
          tna_period: this.selectedPeriod
        };
        
        if (this.filters.search) {
          params.search = this.filters.search;
        }

        if (this.isHSERole && this.filters.company) {
          params.company = this.filters.company;
        }

        this.applyCourseListFilterParams(params);
        
        await this.getCourses(params);
      } catch (error) {
        console.error('Error fetching courses:', error);
        this.$swal({
          title: 'Error',
          text: error.message || 'Daftar pelatihan belum berhasil dimuat. Silakan coba lagi.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
      }
    },
    
    async fetchSummary() {
      if (!this.selectedPeriod) return;
      
      try {
        const params = {
          tna_period: this.selectedPeriod
        };
        
        if (this.isHSERole && this.filters.company) {
          params.company = this.filters.company;
        }
        
        await this.getSummary(params);
      } catch (error) {
        console.error('Error fetching summary:', error);
        this.$swal({
          title: 'Error',
          text: error.message || 'Ringkasan belum berhasil dimuat. Silakan coba lagi.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
      }
    },
    
    viewCourseDetail(course) {
      console.log('📖 View course detail:', course.title);
      this.selectedCourse = course;
      this.$nextTick(() => {
        this.$bvModal.show('modal-mitra-tna-detail');
      });
    },
    
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
    
    onPerPageChange() {
      this.currentPage = 1;
      this.fetchCourses();
    },
    
    clearFilters() {
      this.filters = {
        search: '',
        company: null
      };
      this.currentPage = 1;
    },
    
    handleImageError(event) {
      event.target.src = 'https://dummyimage.com/300x200/FAFAFA/000000&text=Course';
    },
    
    getTrainingTypeIcon(trainingType) {
      const type = trainingType?.toLowerCase() || '';
      
      if (type.includes('certification') && !type.includes('semi')) {
        return 'fe fe-file-text';
      } else if (type.includes('semi')) {
        return 'fe fe-shield';
      } else if (type.includes('training')) {
        return 'fe fe-user';
      }
      
      return 'fe fe-award';
    },
    
    getStatusBadgeClass(status) {
      const classes = {
        'SCHEDULE': 'badge bg-success',
        'NO SCHEDULE': 'badge bg-warning',
        'COMPLETED': 'badge bg-info',
        'CANCELLED': 'badge bg-danger'
      };
      return classes[status] || 'badge bg-secondary';
    },
    
    // formatPeriodLabel(startDate, endDate) {
    //   if (!startDate || !endDate) return '';
      
    //   const start = new Date(startDate);
    //   const end = new Date(endDate);
      
    //   const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
    //   const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
    //   const year = end.getFullYear();
      
    //   return `${startMonth} ${year} - ${endMonth} ${year}`;
    // }
    formatPeriodLabel(startDate, endDate) {
      if (!startDate || !endDate) return '';
      
      const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      };
      
      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    },
    getDefaultPeriodId() {
      if (!Array.isArray(this.periods) || this.periods.length === 0) {
        return null;
      }

      const openPeriod = this.periods.find((period) => {
        return String(period?.vstatus || '').toLowerCase() === 'open';
      });

      return openPeriod?.id || null;
    },

    async downloadTemplate() {
      try {
        this.$swal({
          title: 'Mengunduh...',
          text: 'Mohon tunggu, file template sedang disiapkan',
          allowOutsideClick: false,
          showConfirmButton: false,
          willOpen: () => {
            this.$swal.showLoading();
          }
        });

        const response = await api.downloadTemplateByNik();
        
        // Create blob and download
        const blob = new Blob([response.data], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `template-bulk-upload-${new Date().getTime()}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.$swal({
          title: 'Success',
          text: 'Template berhasil diunduh',
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
      } catch (error) {
        console.error('Error downloading template:', error);
        this.$swal({
          title: 'Error',
          text: error.response?.data?.message || 'Template belum berhasil diunduh. Silakan coba lagi.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
      }
    },

    async uploadInitialData() {
      // Create file input
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.xlsx, .xls';
      
      input.onchange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        const validTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
        if (!validTypes.includes(file.type)) {
          this.$swal({
            title: 'File Tidak Valid',
            text: 'Unggah file Excel (.xlsx atau .xls)',
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#e63757'
          });
          return;
        }

        try {
          this.$swal({
            title: 'Mengunggah...',
            text: 'Mohon tunggu, file sedang diproses',
            allowOutsideClick: false,
            showConfirmButton: false,
            willOpen: () => {
              this.$swal.showLoading();
            }
          });

          const formData = new FormData();
          formData.append('file', file);

          const response = await api.bulkCreateByNik(formData);

          this.$swal({
            title: 'Success',
            text: response.data?.message || 'Data awal berhasil diunggah',
            icon: 'success',
            confirmButtonText: 'OK',
            confirmButtonColor: '#e63757'
          });

          // Refresh data
          this.fetchCourses();
          this.fetchSummary();
        } catch (error) {
          console.error('Error uploading file:', error);
          this.$swal({
            title: 'Error',
            text: error.response?.data?.message || 'File belum berhasil diunggah. Silakan coba lagi.',
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#e63757'
          });
        }
      };

      input.click();
    },
  },
  
  async mounted() {
    console.log('✅ Mitra TNA Report page mounted');
    this.isInitializing = true;
    
    // Ensure profile data is loaded first
    try {
      if (!this.profileData || Object.keys(this.profileData).length === 0) {
        await this.$store.dispatch('users/getProfile');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
    
    // Fetch companies list if HSE role
    if (this.isHSERole) {
      this.fetchCompanies();
    }
    
    // Fetch TNA periods first
    try {
      await this.$store.dispatch('tnaPeriod/getTNAPeriods');
      
      // Set open period as default if available
      const defaultPeriodId = this.getDefaultPeriodId();
      if (defaultPeriodId) {
        this.selectedPeriod = defaultPeriodId;
      }
    } catch (error) {
      console.error('Error fetching periods:', error);
    }
    
    this.isInitializing = false;
    
    await Promise.all([
      this.fetchCourses(),
      this.selectedPeriod ? this.fetchSummary() : Promise.resolve()
    ]);
  },

  beforeDestroy() {
    clearTimeout(this.searchDebounceTimer);
  }
};
